import { Mesh, MeshGeometry, type Texture } from "pixi.js";
import { BALL, FIELD, goalMouth } from "@golazo/shared";

/**
 * La red que se mueve cuando entra el balon.
 *
 * Hasta ahora la red era un rectangulo con textura repetida: el balon cruzaba
 * la linea, se paraba en seco y la porteria no se enteraba. El gol —lo unico
 * que de verdad se celebra en un juego de futbol— no tenia ningun remate
 * visual. Esto lo pone: el paño recibe un impulso en el punto por donde entro
 * el balon y vuelve a su sitio oscilando, con la fuerza que traia el disparo.
 *
 * Es un muelle amortiguado por vertice, no un simulador de telas: cada vertice
 * se desplaza hacia FUERA del campo y vuelve. Con el perimetro clavado al
 * marco (los palos y el fondo) el resultado se lee como una red de verdad, y
 * el coste es un `Float32Array` pequeño que solo se toca mientras haya
 * movimiento.
 *
 * Nada de esto entra en la simulacion: es render puro, no viaja por la red y
 * no afecta al lockstep.
 */

/** Cuanto mundo cubre un tile de red. Mismo valor que el relleno de siempre. */
const NET_TILE_WORLD = 70;

/**
 * Rejilla del paño. Celdas de ~12x17 unidades de mundo: suficiente para que la
 * onda se lea como tela y no como un triangulo estirandose, y lo bastante
 * pequeña para que recalcularla entera salga gratis.
 */
const NX = 7;
const NY = 15;

/** Muelle: `k` manda la velocidad de vuelta, `c` cuanto rebota antes de parar. */
const STIFFNESS = 95;
const DAMPING = 8.2;

/** Tope de desplazamiento hacia fuera, en unidades de mundo. */
const MAX_PUSH = 52;

/**
 * Cuanto del disparo se convierte en empuje. Sale de dividir el tope por una
 * velocidad de disparo fuerte: un cañonazo hunde la red, un gol a puerta vacia
 * la mueve poco. Que se note la diferencia es justo la gracia.
 */
const SPEED_TO_PUSH = MAX_PUSH / 1700;

/** Empuje minimo: hasta el gol mas flojo tiene que mover algo. */
const MIN_PUSH = 10;

/** Por debajo de esto se considera parada y se deja de tocar el buffer. */
const REST_EPSILON = 0.05;

/** Cuanto del empuje entra como velocidad inicial del muelle. */
const IMPULSE_GAIN = STIFFNESS * 0.09;

/** Cuanto se recoge el paño hacia el punto de impacto mientras cede. */
const GATHER = 0.22;

/**
 * Con cuanta fuerza el balon apoyado moldea el paño a su alrededor.
 *
 * Bastante mas rigido que el muelle de reposo (95) a proposito: mientras el
 * balon esta dentro manda el, y la red se le amolda en un par de frames en vez
 * de negociar con su propia elasticidad. Al salir, el muelle recupera el mando
 * solo, sin ningun caso especial.
 */
const CONTACT_STIFFNESS = 260;

/**
 * Paso maximo de integracion. Sin esto, volver a una pestaña en segundo plano
 * trae un `dt` de segundos y el muelle explicito se va a infinito.
 */
const MAX_STEP = 1 / 30;

export interface GoalNet {
  readonly mesh: Mesh;
  /** Impacto a la altura `worldY` con la velocidad que traia el balon. */
  hit(worldY: number, speed: number): void;
  /** Contacto sostenido del balon: ver `NetCloth.press`. */
  press(worldY: number, depth: number): void;
  update(dt: number): void;
}

export interface GoalNets {
  hit(goal: "left" | "right", worldY: number, speed: number): void;
  press(goal: "left" | "right", worldY: number, depth: number): void;
  update(dt: number): void;
}

/**
 * El paño, sin Pixi: posiciones, muelles y nada mas.
 *
 * Separado del `Mesh` a proposito. Lo que puede salir mal aqui —que la red no
 * vuelva al reposo, que el muelle se dispare a infinito, que los bordes se
 * despeguen del marco— es aritmetica, y la aritmetica se prueba en Node. Lo
 * que envuelve esto es solo un buffer de vertices.
 */
export interface NetCloth {
  /** Vertices en coordenadas de campo, `[x0, y0, x1, y1, ...]`. */
  readonly positions: Float32Array;
  readonly uvs: Float32Array;
  readonly indices: Uint32Array;
  hit(worldY: number, speed: number): void;
  /**
   * Contacto SOSTENIDO del balon contra el paño: a que altura empuja y cuanto
   * lo ha hundido. Se llama en cada frame mientras el balon esta dentro.
   *
   * Es lo que separa "la red vibra al marcar" de "la red tiene el balon
   * dentro": el bolsillo se forma donde esta el balon y le sigue si rueda.
   */
  press(worldY: number, depth: number): void;
  /** Devuelve `false` cuando ya esta parada y no hay nada que repintar. */
  step(dt: number): boolean;
  /** Desplazamiento maximo actual, en unidades de mundo. Para los tests. */
  peak(): number;
}

export function createNetCloth(side: "left" | "right"): NetCloth {
  const mouth = goalMouth();
  const depth = FIELD.GOAL_DEPTH;
  const height = mouth.half * 2;
  const x0 = side === "left" ? -depth : FIELD.WIDTH;
  const y0 = mouth.top;
  /** Hacia donde se hunde la red: siempre en direccion contraria al campo. */
  const normal = side === "left" ? -1 : 1;

  const count = NX * NY;
  const positions = new Float32Array(count * 2);
  const rest = new Float32Array(count * 2);
  const uvs = new Float32Array(count * 2);
  /**
   * Cuanto puede moverse cada vertice. El perimetro va clavado al marco y el
   * centro es lo que mas cede: es el modo de vibracion de una membrana sujeta
   * por los bordes, y por eso se lee como tela y no como una lona rigida.
   */
  const freedom = new Float32Array(count);
  const disp = new Float32Array(count);
  const vel = new Float32Array(count);

  const uRepeat = depth / NET_TILE_WORLD;
  const vRepeat = height / NET_TILE_WORLD;

  for (let iy = 0; iy < NY; iy++) {
    const ty = iy / (NY - 1);
    for (let ix = 0; ix < NX; ix++) {
      const tx = ix / (NX - 1);
      const i = iy * NX + ix;
      const px = x0 + tx * depth;
      const py = y0 + ty * height;
      rest[i * 2] = px;
      rest[i * 2 + 1] = py;
      positions[i * 2] = px;
      positions[i * 2 + 1] = py;
      uvs[i * 2] = tx * uRepeat;
      uvs[i * 2 + 1] = ty * vRepeat;
      freedom[i] = Math.sin(Math.PI * tx) * Math.sin(Math.PI * ty);
    }
  }

  const indices = new Uint32Array((NX - 1) * (NY - 1) * 6);
  let k = 0;
  for (let iy = 0; iy < NY - 1; iy++) {
    for (let ix = 0; ix < NX - 1; ix++) {
      const a = iy * NX + ix;
      const b = a + 1;
      const c = a + NX;
      const d = c + 1;
      indices[k++] = a;
      indices[k++] = b;
      indices[k++] = c;
      indices[k++] = b;
      indices[k++] = d;
      indices[k++] = c;
    }
  }

  /** Altura del ultimo impacto: hacia ahi se recoge el paño mientras cede. */
  let impactY = y0 + height / 2;
  let asleep = true;
  /** Contacto vivo del balon este frame: `null` cuando no hay nada dentro. */
  let contact: { y: number; depth: number } | null = null;

  return {
    positions,
    uvs,
    indices,

    hit(worldY: number, speed: number): void {
      impactY = worldY;
      const push = Math.min(MAX_PUSH, MIN_PUSH + speed * SPEED_TO_PUSH);
      // El impulso se reparte alrededor del punto de entrada. `sigma` sale del
      // tamaño de la porteria, no de un numero fijo, para que la onda ocupe lo
      // mismo en 3v3 que en 11v11 (los modos cambian `goalScale`).
      const sigma = Math.max(BALL.RADIUS * 3, mouth.half * 0.5);
      const twoSigmaSq = 2 * sigma * sigma;
      for (let i = 0; i < count; i++) {
        const dy = rest[i * 2 + 1]! - worldY;
        const kernel = Math.exp(-(dy * dy) / twoSigmaSq);
        vel[i] = vel[i]! + freedom[i]! * kernel * push * IMPULSE_GAIN;
      }
      asleep = false;
    },

    press(worldY: number, depth: number): void {
      contact = { y: worldY, depth: Math.max(0, depth) };
      if (depth > 0.5) asleep = false;
    },

    step(dt: number): boolean {
      if (asleep || dt <= 0) return false;

      // El paso se acota: una pestaña que vuelve de segundo plano trae un `dt`
      // enorme, y con un muelle explicito eso no es un salto grande sino una
      // red disparada al infinito.
      const h = Math.min(dt, MAX_STEP);

      /*
       * Forma que el balon le esta pidiendo al paño AHORA mismo. El muelle
       * tira hacia el reposo y el contacto tira hacia aqui: donde se
       * encuentran es el bolsillo alrededor del balon.
       */
      const target = contact;
      const sigma = target ? Math.max(BALL.RADIUS * 2.1, mouth.half * 0.22) : 1;
      const twoSigmaSq = 2 * sigma * sigma;
      // El contacto se consume: si el balon sale, el frame siguiente ya no
      // empuja y la red vuelve sola.
      contact = null;

      let moving = false;
      for (let i = 0; i < count; i++) {
        const d = disp[i]!;
        const prevV = vel[i]!;
        let accel = -STIFFNESS * d - DAMPING * prevV;

        if (target) {
          // Bolsillo local: hondo justo donde esta el balon y desvanecido a
          // dos radios de distancia. `freedom` sigue mandando en los bordes,
          // asi que el paño nunca se despega del marco.
          const dy = rest[i * 2 + 1]! - target.y;
          const want = freedom[i]! * Math.exp(-(dy * dy) / twoSigmaSq) * target.depth;
          accel += (want - d) * CONTACT_STIFFNESS;
        }

        const v = prevV + accel * h;
        const next = d + v * h;
        vel[i] = v;
        disp[i] = next;
        if (Math.abs(next) > REST_EPSILON || Math.abs(v) > REST_EPSILON) moving = true;

        const restX = rest[i * 2]!;
        const restY = rest[i * 2 + 1]!;
        positions[i * 2] = restX + normal * next;
        // El paño tambien se recoge un poco hacia el impacto: sin esto la red
        // se hunde pero no se estira, que es lo que la delata como rectangulo.
        positions[i * 2 + 1] = restY + (impactY - restY) * (next / MAX_PUSH) * GATHER;
      }

      if (!moving) {
        // Parada: se devuelve al reposo exacto y se deja de escribir el buffer
        // hasta el proximo gol.
        for (let i = 0; i < count; i++) {
          disp[i] = 0;
          vel[i] = 0;
          positions[i * 2] = rest[i * 2]!;
          positions[i * 2 + 1] = rest[i * 2 + 1]!;
        }
        asleep = true;
      }

      return true;
    },

    peak(): number {
      let max = 0;
      for (let i = 0; i < count; i++) max = Math.max(max, Math.abs(disp[i]!));
      return max;
    },
  };
}

function createNet(side: "left" | "right", texture: Texture): GoalNet {
  const cloth = createNetCloth(side);
  const geometry = new MeshGeometry({
    positions: cloth.positions,
    uvs: cloth.uvs,
    indices: cloth.indices,
  });
  const mesh = new Mesh({ geometry, texture });
  mesh.label = `net-${side}`;
  // Opaca del todo. A media transparencia la malla se fundia con el hueco
  // oscuro de la porteria y practicamente no se veia — y una red que no se ve
  // no puede moverse de forma que se note.
  mesh.alpha = 1;

  const buffer = geometry.getBuffer("aPosition");

  return {
    mesh,
    hit: (worldY, speed) => cloth.hit(worldY, speed),
    press: (worldY, depth) => cloth.press(worldY, depth),
    update(dt: number): void {
      if (cloth.step(dt)) buffer.update();
    },
  };
}

/**
 * Las dos redes. Devuelve `null` si no hay textura: el juego tiene que seguir
 * arrancando sin los PNG, igual que el resto de capas con textura.
 */
export function createGoalNets(texture: Texture | null): {
  nets: GoalNets;
  meshFor(side: "left" | "right"): Mesh | null;
} {
  if (!texture) {
    return {
      nets: { hit: () => {}, press: () => {}, update: () => {} },
      meshFor: () => null,
    };
  }

  // La malla lee la textura con coordenadas mayores que 1 para repetirla; sin
  // esto se estiraria un solo tile sobre toda la porteria.
  texture.source.addressMode = "repeat";

  const left = createNet("left", texture);
  const right = createNet("right", texture);

  return {
    nets: {
      hit(goal, worldY, speed): void {
        (goal === "left" ? left : right).hit(worldY, speed);
      },
      press(goal, worldY, depth): void {
        (goal === "left" ? left : right).press(worldY, depth);
      },
      update(dt): void {
        left.update(dt);
        right.update(dt);
      },
    },
    meshFor: (side) => (side === "left" ? left.mesh : right.mesh),
  };
}
